import * as d3 from "d3";

export default class MitNavDrawingService {
    constructor(mapService) {
        this.mapService = mapService;
        this.navDrawingState = {
            active: false,
            points: [], 
            tempLine: null,
            tempLineGroup: null,
            startPointId: null
        };
    }

    setupGlobalEvents() {
        this.mapService.svg.on("mousemove.nav", (event) => {
            if (this.navDrawingState.active && this.navDrawingState.tempLineGroup) {
                
                let mousePos = d3.pointer(event, this.mapService.layerMap.node());
                
                let gisPoint = this.mapService.projection.invert(mousePos);
                let pts = [...this.navDrawingState.points, gisPoint];
                let lineGenerator = d3.line().x(d => this.mapService.projection(d)[0]).y(d => this.mapService.projection(d)[1]);
                this.navDrawingState.tempLineGroup.attr("d", lineGenerator(pts));
            }
        });

        this.mapService.svg.on("dblclick.nav", (event) => {
            if (this.navDrawingState.active) {
                event.preventDefault();
                
                // FIX: Get coordinates relative to the transformed map layer
                let mousePos = d3.pointer(event, this.mapService.layerMap.node());
                
                let parentSpace = this.mapService.findParentSpace(mousePos[0], mousePos[1]);
                if (!parentSpace) {
                    let allSpaces = this.mapService.layerSpaces.selectAll('path').data();
                    if(allSpaces.length > 0) parentSpace = allSpaces[0];
                }
                if(!parentSpace) return;

                let gisPoint = this.mapService.projection.invert(mousePos);
                let endPtObj = { type: 'END_POINT', name: 'End Point' };
                let endPtFeat = this.drawNavPoint(endPtObj, gisPoint, parentSpace, true);
                
                this.navDrawingState.points.push(gisPoint);
                this.finalizeNavLine(endPtFeat.properties.mitId, parentSpace);
            }
        });
    }

    renderExistingNavData(navFeatures) {
        if (!navFeatures || !Array.isArray(navFeatures)) return;

        // 1. DOMAIN LOGIC: Filter the features based on the MapService's current floor
        let filteredFeatures = navFeatures.filter(f => {
            if (!f.properties) return false;
            
            // Level Changes (Elevators/Stairs) span multiple floors
            if (f.properties.useType === 'LEVEL_CHANGE') {
                return f.properties.accessToLevels && f.properties.accessToLevels.includes(this.mapService.selectedFloor);
            }
            
            // Standard paths and points belong to a single floor
            return f.properties.level === this.mapService.selectedFloor;
        });
        
        this.mapService.layerNavLines.selectAll("*").remove();
        this.mapService.layerNavPoints.selectAll("*").remove();

        // Use filteredFeatures, and fallback to native geometry checks for safety!
        let lines = filteredFeatures.filter(f => f.properties.spaceType === 'nav_line' || f.geometry.type === 'LineString');
        let points = filteredFeatures.filter(f => f.properties.spaceType === 'nav_point' || f.geometry.type === 'Point');

        let lineGenerator = d3.line().x(d => this.mapService.projection(d)[0]).y(d => this.mapService.projection(d)[1]);

        // Key function d => d.properties.mitId kept for D3 visual stability
        let pathSelection = this.mapService.layerNavLines.selectAll("path")
            .data(lines, d => d.properties.mitId)
            .enter().append("path")
            .attr("d", d => lineGenerator(d.geometry.coordinates))
            .attr("id", f => 'mit_'+f.properties.mitId)
            .attr("stroke", "darkred")
            .attr("stroke-width", 4)
            .attr("fill", "none")
            .style("pointer-events", "stroke");
        
        this._bindLineEvents(pathSelection);

        let self = this;
        this.mapService.layerNavPoints.selectAll("circle")
            .data(points, d => d.properties.mitId)
            .enter().append("circle")
            .attr("cx", d => this.mapService.projection(d.geometry.coordinates)[0])
            .attr("cy", d => this.mapService.projection(d.geometry.coordinates)[1])
            .attr("r", d => {
                if (d.properties.useType === 'NAV_ROOM_ENTRANCE') return 3.5; // 30% smaller
                return (d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT') ? 5 : 8;
            })
            .attr("id", f => 'mit_'+f.properties.mitId)
            .attr("fill", d => {
                if(d.properties.useType === 'NAV_ROOM_ENTRANCE') return "#424242"; // Dark gray hinge
                if(d.properties.useType === 'END_POINT') return "red";
                if(d.properties.useType === 'QR_POINT') return "blue";
                if(d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT') return "grey";
                return "transparent";
            })
            .attr("stroke", d => d.properties.useType === 'LEVEL_CHANGE' ? "red" : "white")
            .attr("stroke-width", 2)
            .attr("cursor", "pointer")
            .on("mouseover", function(event, d) {
                if (self.mapService.objectToCreate && self.mapService.objectToCreate.type === 'PATH') {
                    d3.select(this).attr("r", 12).attr("stroke", "yellow").attr("stroke-width", 3);
                }
            })
            .on("mouseout", function(event, d) {
                d3.select(this)
                    .attr("r", (d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT' || d.properties.useType === 'NAV_ROOM_ENTRANCE') ? 5 : 8)
                    .attr("stroke", d.properties.useType === 'LEVEL_CHANGE' ? "red" : "white")
                    .attr("stroke-width", 2);
            })
            .on("click", (event, obj) => this.handleNavPointClick(event, obj))
            .call(d3.drag()
                .on("start", (event, space) => this.mapService.dragStartEvent(event, space))
                .on("drag", (event, space) => this.dragNavPointEvent(event, space))
                .on("end", (event, space) => this.mapService.dragEndEvent(event, space))
            );

    }

    _getDistanceSq(p1, p2) {
        return Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2);
    }

    _projectPointOnSegment(p, a, b) {
        let v = [b[0] - a[0], b[1] - a[1]];
        let w = [p[0] - a[0], p[1] - a[1]];
        let c1 = w[0]*v[0] + w[1]*v[1];
        if (c1 <= 0) return a;
        let c2 = v[0]*v[0] + v[1]*v[1];
        if (c2 === 0) return a; 
        if (c2 <= c1) return b;
        let b_prop = c1 / c2;
        return [a[0] + b_prop * v[0], a[1] + b_prop * v[1]];
    }

    _bindLineEvents(selection) {
        let self = this;
        selection.attr("cursor", "pointer")
            .on("mouseover", function(event, d) {
                if (self.mapService.objectToCreate && self.mapService.objectToCreate.type === 'PATH') {
                    d3.select(this).attr("stroke-width", 8).attr("stroke", "red");
                }
            })
            .on("mouseout", function(event, d) {
                d3.select(this).attr("stroke-width", 4).attr("stroke", "darkred");
            })
            .on("click", function(event, lineData) {
                if (!self.mapService.editModeAllowModify) {
                    if(self.mapService.editmode) self.mapService.serviceSelection.select(lineData, d3.select(this));
                    return;
                }
                event.stopPropagation();
                
                let mousePos = d3.pointer(event);
                let gisPoint = self.mapService.projection.invert(mousePos);
                
                let parentSpace = self.mapService.findParentSpace(mousePos[0], mousePos[1]);
                if (!parentSpace) {
                    let allSpaces = self.mapService.layerSpaces.selectAll('path').data();
                    if(allSpaces.length > 0) parentSpace = allSpaces[0];
                }
                if(!parentSpace) return;

                // Action Router
                if (self.mapService.objectToCreate && (self.mapService.objectToCreate.type === 'PATH' || self.mapService.objectToCreate.type === 'CONNECTION_POINT')) {
                    
                    // ISOLATED ACTION: Split Line
                    self.splitNavLine(this, lineData, gisPoint, parentSpace);
                
                } else if (self.mapService.objectToCreate && self.mapService.objectToCreate.type === 'QR_POINT') {
                     self.drawNavPoint(self.mapService.objectToCreate, gisPoint, parentSpace, false);
                } else {
                    if(self.mapService.editmode) self.mapService.serviceSelection.select(lineData, d3.select(this));
                }
            });
    }

    // ==========================================
    // ISOLATED ACTION: SPLIT NAVIGATION LINE
    // ==========================================
    splitNavLine(domElement, lineData, gisPoint, parentSpace) {
        // 1. Math Projection: Project click perfectly onto the exact line segment
        let coords = lineData.geometry.coordinates;
        let minDist = Infinity;
        let bestProj = gisPoint;
        let insertIndex = 0;
        
        for (let i = 0; i < coords.length - 1; i++) {
            let proj = this._projectPointOnSegment(gisPoint, coords[i], coords[i+1]);
            let dist = this._getDistanceSq(gisPoint, proj);
            if (dist < minDist) {
                minDist = dist;
                bestProj = proj;
                insertIndex = i;
            }
        }

        // 2. Create the interceding Connection Point / Path Node
        let nodeObj = { 
            type: this.mapService.objectToCreate.type === 'CONNECTION_POINT' ? 'CONNECTION_POINT' : 'PATH_NODE', 
            name: this.mapService.objectToCreate.type === 'CONNECTION_POINT' ? 'Connection Point' : 'Path Node' 
        };
        let nodeFeat = this.drawNavPoint(nodeObj, bestProj, parentSpace, true);

        // 3. Split the Coordinates Array
        let coordsPart1 = coords.slice(0, insertIndex + 1);
        coordsPart1.push(bestProj);
        
        let coordsPart2 = [bestProj];
        coordsPart2.push(...coords.slice(insertIndex + 1));

        // 4. DIRECT IN-PLACE MUTATION: Shrink the original line (Part 1)
        let oldEndId = lineData.properties.endPointId;
        lineData.properties.endPointId = nodeFeat.properties.mitId;
        lineData.geometry.coordinates = coordsPart1;
        
        let lineGen = d3.line().x(d => this.mapService.projection(d)[0]).y(d => this.mapService.projection(d)[1]);
        
        // Update Part 1 visual
        d3.select(domElement).attr("d", lineGen(lineData.geometry.coordinates));
        this.mapService.editObjectCallback(this.mapService.EDIT_OBJECT_TYPE.MODIFIED, lineData, d3.select(domElement));

        // 5. Create a brand new line for Part 2
        let newLineInfo = this.mapService.getObjectInfo(this.mapService.existingObjectMitIdsList, parentSpace.properties.mitBuildingId, 'PATH', 'Path');
        this.mapService.existingObjectMitIdsList[newLineInfo.mitId] = 1;

        let newLineFeat = {
            type: "Feature",
            geometry: { type: "LineString", coordinates: coordsPart2 },
            properties: {
                ...lineData.properties, 
                mitId: newLineInfo.mitId,
                mitIdShort: newLineInfo.mitIdShort,
                startPointId: nodeFeat.properties.mitId,
                endPointId: oldEndId
            }
        };

        delete newLineFeat.properties.id;
        delete newLineFeat.properties.version;
        
        let newLineSelection = this.mapService.layerNavLines.append("path")
            .datum(newLineFeat)
            .attr("d", lineGen(coordsPart2))
            .attr("id", f => 'mit_'+f.properties.mitId)
            .attr("stroke", "darkred")
            .attr("stroke-width", 4)
            .attr("fill", "none")
            .style("pointer-events", "stroke");

        this._bindLineEvents(newLineSelection);
        this.mapService.editObjectCallback(this.mapService.EDIT_OBJECT_TYPE.CREATED, newLineFeat, newLineSelection);

        // 6. Handle drawing tool follow-through
        if (this.mapService.objectToCreate.type === 'PATH') {
            if (!this.navDrawingState.active) {
                this.startNavLine(nodeFeat);
            } else {
                this.navDrawingState.points.push(bestProj);
                this.finalizeNavLine(nodeFeat.properties.mitId, parentSpace);
            }
        } else if (this.mapService.objectToCreate.type === 'CONNECTION_POINT') {
            this.mapService.objectToCreate = null; 
        }
    }

    drawNavPoint(objToCreate, gisPoint, parentSpace, skipSelect = false) {
        let objInfo = this.mapService.getObjectInfo(this.mapService.existingObjectMitIdsList, parentSpace.properties.mitBuildingId, objToCreate.type, objToCreate.name);
        
        this.mapService.existingObjectMitIdsList[objInfo.mitId] = 1;

        let feature = {
            type: "Feature",
            geometry: { type: "Point", coordinates: gisPoint },
            properties: {
                mitId: objInfo.mitId,
                mitIdShort: objInfo.mitIdShort,
                mitBuildingId: parentSpace.properties.mitBuildingId,
                name: objInfo.name,
                level: this.mapService.selectedFloor, 
                useType: objToCreate.type,
                spaceType: 'nav_point'
            }
        };

        if (objToCreate.type === 'LEVEL_CHANGE') {
            feature.properties.accessToLevels = [this.mapService.selectedFloor];
            feature.properties.levelChangeType = 'Stairs'; // Set default routing type
        }

        let self = this;
        let pt = this.mapService.layerNavPoints.append("circle");
        pt.datum(feature)
          .attr("cx", d => this.mapService.projection(d.geometry.coordinates)[0])
          .attr("cy", d => this.mapService.projection(d.geometry.coordinates)[1])
          .attr("r", d => {
              if (d.properties.useType === 'NAV_ROOM_ENTRANCE') return 3.5;
              return (d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT') ? 5 : 8;
          })
          .attr("id", f => 'mit_'+f.properties.mitId)
          .attr("fill", d => {
              if(d.properties.useType === 'NAV_ROOM_ENTRANCE') return "#424242";
              if(d.properties.useType === 'END_POINT') return "red";
              if(d.properties.useType === 'QR_POINT') return "blue";
              if(d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT') return "grey";
              return "transparent";
          })
          .attr("stroke", d => d.properties.useType === 'LEVEL_CHANGE' ? "red" : "white")
          .attr("stroke-width", 2)
          .attr("cursor", "pointer")
          .on("mouseover", function(event, d) {
                if (self.mapService.objectToCreate && self.mapService.objectToCreate.type === 'PATH') {
                    d3.select(this).attr("r", 12).attr("stroke", "yellow").attr("stroke-width", 3);
                }
            })
          .on("mouseout", function(event, d) {
                d3.select(this)
                    .attr("r", (d.properties.useType === 'PATH_NODE' || d.properties.useType === 'CONNECTION_POINT' || d.properties.useType === 'NAV_ROOM_ENTRANCE') ? 5 : 8)
                    .attr("stroke", d.properties.useType === 'LEVEL_CHANGE' ? "red" : "white")
                    .attr("stroke-width", 2);
            })
          .on("click", (event, obj) => this.handleNavPointClick(event, obj))
          .call(d3.drag()
                .on("start", (event, space) => this.mapService.dragStartEvent(event, space))
                .on("drag", (event, space) => this.dragNavPointEvent(event, space))
                .on("end", (event, space) => this.mapService.dragEndEvent(event, space))
          );


        if(!skipSelect && objToCreate.type !== 'PATH_NODE') this.mapService.serviceSelection.select(feature, pt);
        this.mapService.editObjectCallback(this.mapService.EDIT_OBJECT_TYPE.CREATED, feature, pt);
        return feature;
    }

    handleNavPointClick(event, pointFeature) {
        if (!this.mapService.editModeAllowModify) return;
        event.stopPropagation();

        if (this.mapService.objectToCreate && this.mapService.objectToCreate.type === 'PATH') {
            if (!this.navDrawingState.active) {
                this.startNavLine(pointFeature);
            } else {
                // ADDED: Strict validation at the time of connection
                let isValidConnection = false;
                let pathLevel = this.navDrawingState.level;

                if (pointFeature.properties.useType === 'LEVEL_CHANGE') {
                    // It's a stair/elevator, so it just needs to have access to this floor
                    isValidConnection = pointFeature.properties.accessToLevels && pointFeature.properties.accessToLevels.includes(pathLevel);
                } else {
                    // It's a standard point, so the level must strictly match
                    isValidConnection = (pointFeature.properties.level === pathLevel);
                }

                if (!isValidConnection) {
                    alert("Data Error: You cannot connect a path to a node on a different floor.");
                    return; // Abort the connection
                }

                this.navDrawingState.points.push(pointFeature.geometry.coordinates);
                this.finalizeNavLine(pointFeature.properties.mitId, pointFeature);
            }
        } else {
            this.mapService.serviceSelection.select(pointFeature, d3.select(event.target));
        }
    }

    startNavLine(pointFeature) {
        this.navDrawingState.active = true;
        this.navDrawingState.startPointId = pointFeature.properties.mitId;
        // Seed the path's required level at creation time
        this.navDrawingState.level = pointFeature.properties.level; 
        this.navDrawingState.points = [pointFeature.geometry.coordinates];
        
        this.navDrawingState.tempLineGroup = this.mapService.layerNavLines.append("path")
            .attr("class", "temp-nav-line")
            .attr("stroke", "darkred")
            .attr("stroke-width", 3)
            .attr("fill", "none")
            .style("pointer-events", "none");
    }

    updateTempNavLine() {
        if (!this.navDrawingState.active || !this.navDrawingState.tempLineGroup) return;
        let lineGenerator = d3.line()
            .x(d => this.mapService.projection(d)[0])
            .y(d => this.mapService.projection(d)[1]);
        this.navDrawingState.tempLineGroup.attr("d", lineGenerator(this.navDrawingState.points));
    }

    finalizeNavLine(endPointId, parentSpace) {
        let objInfo = this.mapService.getObjectInfo(this.mapService.existingObjectMitIdsList, parentSpace.properties.mitBuildingId, 'PATH', 'Path');
        
        this.mapService.existingObjectMitIdsList[objInfo.mitId] = 1;

        let feature = {
            type: "Feature",
            geometry: { type: "LineString", coordinates: [...this.navDrawingState.points] },
            properties: {
                mitId: objInfo.mitId,
                mitIdShort: objInfo.mitIdShort,
                mitBuildingId: parentSpace.properties.mitBuildingId,
                name: objInfo.name,
                level: this.mapService.selectedFloor, 
                useType: 'PATH',
                spaceType: 'nav_line',
                startPointId: this.navDrawingState.startPointId,
                endPointId: endPointId
            }
        };

        let line = this.mapService.layerNavLines.append("path");
        let lineGenerator = d3.line().x(d => this.mapService.projection(d)[0]).y(d => this.mapService.projection(d)[1]);
        
        line.datum(feature)
            .attr("d", lineGenerator(feature.geometry.coordinates))
            .attr("id", f => 'mit_'+f.properties.mitId)
            .attr("stroke", "darkred")
            .attr("stroke-width", 4)
            .attr("fill", "none");
            
        this._bindLineEvents(line);

        this.mapService.editObjectCallback(this.mapService.EDIT_OBJECT_TYPE.CREATED, feature, line);
        this.cancelNavDrawing();
    }

    cancelNavDrawing() {
        if (this.navDrawingState.tempLineGroup) {
            this.navDrawingState.tempLineGroup.remove();
        }
        this.navDrawingState = { active: false, points: [], tempLine: null, tempLineGroup: null, startPointId: null };
    }

    dragNavPointEvent(event, space) {
        if(this.mapService.editModeAllowModify){
            space.dragAccumulatedX = (space.dragAccumulatedX || 0) + event.dx;
            space.dragAccumulatedY = (space.dragAccumulatedY || 0) + event.dy;
            space.dragX = (space.dragX || 0) + event.dx;
            space.dragY = (space.dragY || 0) + event.dy;
            space.dragControl.attr("transform", "translate(" + space.dragAccumulatedX + "," + space.dragAccumulatedY + ")");

            let gisPoint = this.mapService.projection.invert([
                this.mapService.projection(space.geometry.coordinates)[0] + space.dragX,
                this.mapService.projection(space.geometry.coordinates)[1] + space.dragY
            ]);

            let lineGenerator = d3.line().x(d => this.mapService.projection(d)[0]).y(d => this.mapService.projection(d)[1]);

            this.mapService.layerNavLines.selectAll("path").filter(d => {
                if (!d || !d.properties) return false;
                return d.properties.startPointId === space.properties.mitId || d.properties.endPointId === space.properties.mitId;
            })
            .each((d, i, nodes) => {
                let elem = d3.select(nodes[i]);
                if (d.properties.startPointId === space.properties.mitId) {
                    d.geometry.coordinates[0] = gisPoint;
                }
                if (d.properties.endPointId === space.properties.mitId) {
                    d.geometry.coordinates[d.geometry.coordinates.length - 1] = gisPoint;
                }
                elem.attr("d", lineGenerator(d.geometry.coordinates));
                this.mapService.editObjectCallback(this.mapService.EDIT_OBJECT_TYPE.MODIFIED, d, elem);
            });
        }
    }
}